export type PrintDocumentOptions = {
  autoPrint?: boolean;
  minimumDelayMs?: number;
  onError?: (error: Error) => void;
  onPopupBlocked?: () => void;
  onReady?: (printWindow: Window) => void;
  readinessTimeoutMs?: number;
  windowFeatures?: string;
};

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

const reportError = (error: unknown, onError?: (error: Error) => void) => {
  const normalized = toError(error);
  if (onError) {
    onError(normalized);
  } else {
    console.error('Unable to prepare document for printing.', normalized);
  }
};

const closeWindow = (printWindow: Window) => {
  if (!printWindow.closed && typeof printWindow.close === 'function') printWindow.close();
};

const delay = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

// Returns null when the document is already ready so printing can stay synchronous.
const getPendingImages = (doc: Document) =>
  Array.from(doc.images ?? []).filter((image) => !image.complete);

const waitForPrintReadiness = (doc: Document, timeoutMs: number): Promise<void> | null => {
  const images = getPendingImages(doc);
  const fonts = doc.fonts;
  if (images.length === 0 && !fonts) return null;
  return new Promise<void>((resolve) => {
    let pending = images.length + (fonts ? 1 : 0);

    let settled = false;
    const cleanup: Array<() => void> = [];
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      cleanup.forEach((removeListener) => removeListener());
      resolve();
    };
    const finishResource = () => {
      if (settled) return;
      pending -= 1;
      if (pending === 0) finish();
    };
    const timeout = window.setTimeout(finish, timeoutMs);

    images.forEach((image) => {
      let imageSettled = false;
      const onImageReady = () => {
        if (imageSettled) return;
        imageSettled = true;
        finishResource();
      };
      image.addEventListener('load', onImageReady);
      image.addEventListener('error', onImageReady);
      cleanup.push(() => {
        image.removeEventListener('load', onImageReady);
        image.removeEventListener('error', onImageReady);
      });
      if (image.complete) onImageReady();
    });

    if (fonts) {
      void Promise.resolve(fonts.ready).then(finishResource, finishResource);
    }
  });
};

const waitUntilReady = async (doc: Document, options: PrintDocumentOptions) => {
  const readiness = waitForPrintReadiness(doc, options.readinessTimeoutMs ?? 10000);
  if (readiness) await readiness;
  if (options.minimumDelayMs) await delay(options.minimumDelayMs);
};

const needsWaiting = (doc: Document, options: PrintDocumentOptions) =>
  Boolean(options.minimumDelayMs) || Boolean(doc.fonts) || getPendingImages(doc).length > 0;

const printNow = (printWindow: Window, options: PrintDocumentOptions) => {
  if (printWindow.closed) return;
  options.onReady?.(printWindow);
  printWindow.focus();
  printWindow.print();
};

const printWhenReady = async (
  printWindow: Window,
  options: PrintDocumentOptions,
): Promise<boolean> => {
  try {
    await waitUntilReady(printWindow.document, options);
    printNow(printWindow, options);
    return true;
  } catch (error) {
    reportError(error, options.onError);
    return false;
  }
};

export const renderAndPrintInWindow = async (
  printWindow: Window,
  html: string,
  options: PrintDocumentOptions = {},
): Promise<boolean> => {
  try {
    const doc = printWindow.document;
    doc.open();
    doc.write(html);
    doc.close();

    if (options.autoPrint === false) {
      await waitUntilReady(doc, options);
      options.onReady?.(printWindow);
      return true;
    }
    if (!needsWaiting(doc, options)) {
      printNow(printWindow, options);
      return true;
    }
    const printed = await printWhenReady(printWindow, options);
    if (!printed) closeWindow(printWindow);
    return printed;
  } catch (error) {
    reportError(error, options.onError);
    closeWindow(printWindow);
    return false;
  }
};

export const openAndPrintHtml = async (
  html: string,
  options: PrintDocumentOptions = {},
): Promise<Window | null> => {
  const printWindow = window.open('', '_blank', options.windowFeatures);
  if (!printWindow) {
    if (options.onPopupBlocked) {
      options.onPopupBlocked();
    } else {
      window.alert('Браузер заблокував вікно друку. Дозвольте спливаючі вікна та спробуйте ще раз.');
    }
    return null;
  }

  const rendered = await renderAndPrintInWindow(printWindow, html, options);
  return rendered ? printWindow : null;
};

export const printHtmlInHiddenFrame = (
  html: string,
  options: Omit<PrintDocumentOptions, 'autoPrint' | 'onPopupBlocked' | 'onReady' | 'windowFeatures'> = {},
) => {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';

  let cleanedUp = false;
  const cleanup = () => {
    if (cleanedUp) return;
    cleanedUp = true;
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };
  const handleLoad = () => {
    iframe.removeEventListener('load', handleLoad);
    const printWindow = iframe.contentWindow;
    if (!printWindow) {
      cleanup();
      reportError(new Error('The print frame did not expose a window.'), options.onError);
      return;
    }

    printWindow.addEventListener('afterprint', cleanup, { once: true });
    void printWhenReady(printWindow, options).then((printed) => {
      if (!printed) cleanup();
    });
  };
  const handleError = () => {
    cleanup();
    reportError(new Error('The print frame failed to load.'), options.onError);
  };

  iframe.addEventListener('load', handleLoad, { once: true });
  iframe.addEventListener('error', handleError, { once: true });
  iframe.srcdoc = html;
  document.body.appendChild(iframe);
};
